import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentLanguage, useTranslator } from 'tradera-lang/translate';
import Tags from '../tags/tags';
import { fetchTrendingSearchesIfNeeded } from 'tradera-state/views/start-page/actions';
import { fetchPersonalisedSearchesIfNeeded } from 'tradera-state/personalisation/actions';
import './personalised-tags.scss';
import { translationDynamicApiClient } from 'tradera-utils/http';
import { isSwedishOrUndefined } from 'tradera-utils/languagecode';
import { selectAutomaticTranslationPreference } from 'tradera-state/language/selectors';
import qs from 'qs';

const PersonalisedSearchesTags = ({ scroll }) => {
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const showFallback = useShowPersonalisedSearchesFallback();
    const { language } = useCurrentLanguage();
    const automaticTranslationPreference = useSelector(
        selectAutomaticTranslationPreference
    );

    const stateTags = useSelector(state =>
        showFallback
            ? state.views.startPage.trendingSearches.items
            : state.personalisation.searches.items
    );
    const [tags, setTags] = useState([]);

    useEffect(() => {
        dispatch(
            showFallback
                ? fetchTrendingSearchesIfNeeded()
                : fetchPersonalisedSearchesIfNeeded()
        );
    }, [dispatch, showFallback]);

    const shouldTranslateOnLoad =
        automaticTranslationPreference && !isSwedishOrUndefined(language);

    useEffect(() => {
        const translateTags = async () => {
            if (!stateTags || !stateTags.length) {
                return;
            }

            const { htmlTexts } = await translationDynamicApiClient.command(
                'gettranslatedtexts',
                {
                    htmlTexts: stateTags.map(x => x.name),
                    toLanguageCodeIso2: language,
                    fromLanguageCodeIso2: 'sv',
                    type: 6
                }
            );
            if (htmlTexts) {
                setTags(
                    htmlTexts.map((text, index) => {
                        const tag = stateTags.find(x => x.index === index + 1);

                        const [pathname, search] = tag.url.split('?');
                        const searchParams = qs.parse(search, {
                            ignoreQueryPrefix: true
                        });
                        const url = `${pathname}?${qs.stringify({
                            ...searchParams,
                            q: text
                        })}`;
                        return {
                            ...tag,
                            name: text,
                            url
                        };
                    })
                );
            }
        };
        if (shouldTranslateOnLoad) {
            translateTags();
        } else {
            setTags(stateTags);
        }
    }, [shouldTranslateOnLoad, language, stateTags]);

    const isLoading = useIsPersonalisedSearchesTagsLoading();
    if (isLoading) {
        // Approximated height of component when loaded
        return <div className="personalised-tags-loading" />;
    }

    return (
        <>
            <h3 className="start-page-section-title">
                {showFallback
                    ? t('trendingTags_searches')
                    : t('personalised_suggestions_searches')}
            </h3>
            <Tags
                gaCategory={
                    showFallback
                        ? 'Personalised Searches fallback'
                        : 'Personalised Searches'
                }
                gaAction="Navigation"
                tags={tags}
                scroll={scroll}
            />
        </>
    );
};

export const useShowPersonalisedSearchesFallback = () =>
    useSelector(
        state => state.personalisation.searches.status === 'HAS_NO_DATA'
    );

export const useIsPersonalisedSearchesTagsLoading = () => {
    const status = useSelector(state => state.personalisation.searches.status);
    const fallbackStatus = useSelector(
        state => state.views.startPage.trendingSearches.status
    );

    if (status === 'HAS_DATA') return false;
    if (status === 'HAS_NO_DATA') {
        if (fallbackStatus === 'HAS_DATA' || fallbackStatus === 'HAS_NO_DATA')
            return false;
        return true;
    }
    return true;
};

PersonalisedSearchesTags.defaultProps = {
    scroll: false
};

PersonalisedSearchesTags.propTypes = {
    scroll: PropTypes.bool
};

export default PersonalisedSearchesTags;
